import { supportedLocales } from "@/lib/i18n-core.mjs";

type Locale = (typeof supportedLocales)[number];

export type LocaleCatalog = Record<string, string>;

// Each specifier resolves to src/locales/<locale>.json.ts, which reassembles
// the per-namespace parts under src/locales/<locale>/.
const catalogLoaders: Record<Locale, () => Promise<LocaleCatalog>> = {
  en: () => import("@/locales/en.json").then((module) => module.default),
  es: () => import("@/locales/es.json").then((module) => module.default),
  fr: () => import("@/locales/fr.json").then((module) => module.default),
  sw: () => import("@/locales/sw.json").then((module) => module.default),
  tlh: () => import("@/locales/tlh.json").then((module) => module.default),
  "zh-CN": () =>
    import("@/locales/zh-CN.json").then((module) => module.default),
};

const catalogCache = new Map<Locale, Promise<LocaleCatalog>>();

// The provider's starting catalog and t()'s English fallback are both read
// synchronously, so settled catalogs are kept beside their promises. Reading
// them back out is what lets a catalog stay dynamically imported; a static
// import would pin it into the entry chunk.
const settledCatalogs = new Map<Locale, LocaleCatalog>();
const noCatalog: LocaleCatalog = {};

export function loadLocaleCatalog(locale: Locale) {
  const cached = catalogCache.get(locale);
  if (cached) return cached;
  const pending = catalogLoaders[locale]()
    .then((catalog) => {
      settledCatalogs.set(locale, catalog);
      return catalog;
    })
    .catch((error) => {
      // A rejected import must not poison the cache, or every later retry
      // replays the same failure. Drop it so a deploy settling or a network
      // blip clearing lets the next load re-fetch the chunk.
      catalogCache.delete(locale);
      throw error;
    });
  catalogCache.set(locale, pending);
  return pending;
}

// A stable empty catalog, so callers that cache against the result's identity
// do not rebuild on every miss.
export function getLoadedCatalog(locale: Locale) {
  return settledCatalogs.get(locale) ?? noCatalog;
}

// A catalog fetch can 404 after a deploy prunes its hashed chunk, or fail on a
// network blip. Neither should blank the app: fall back to English so the
// provider still has strings, and resolve anyway if even English is gone (t()
// then echoes the key). Failed loads are evicted above, so switching back to
// this locale later re-fetches its chunk.
export async function loadPreferredCatalog(locale: Locale) {
  try {
    await loadLocaleCatalog(locale);
  } catch {
    if (locale !== "en") await loadLocaleCatalog("en").catch(() => {});
  }
}
